import { hasValidConsent, recordFirstInteraction } from '@achieve/cx-event-horizon/browser'

const BANNER_SELECTOR = '.consent-banner'
const BANNER_DISMISS_SELECTOR = '[data-consent-dismiss]'
const BUTTON_SELECTOR =
  'button, [role="button"], input[type="submit"], input[type="button"], input[type="image"], input[type="checkbox"], input[type="radio"], [role="checkbox"], [role="radio"]'

/**
 * Maps a click target to an EH consent source. Mirrors
 * `@achieve/cx-event-horizon` initializeFirstInteraction, except that clicks inside the consent
 * banner never count: dismiss controls are recorded as banner_ok / banner_close by the banner
 * handlers, and the privacy-policy link must not grant consent (ADR-011).
 *
 * @param {Element | null} target
 * @returns {'navigation_click' | 'button_click' | 'input_click' | null}
 */
export function resolveConsentSource(target) {
  if (!target?.closest) return null
  if (target.closest(BANNER_DISMISS_SELECTOR)) return null
  if (target.closest(`${BANNER_SELECTOR} a`)) return null
  if (target.closest('a[href]')) return 'navigation_click'
  if (target.closest(BUTTON_SELECTOR)) return 'button_click'
  if (target.closest('input, textarea, select')) return 'input_click'
  return null
}

/**
 * Records the first qualifying click as EH consent, then stops listening.
 * @returns {() => void} cleanup
 */
export function initializeConsentFirstInteraction({ analytics, cookieName } = {}) {
  const noop = () => {}
  if (typeof window === 'undefined') return noop
  if (hasValidConsent(cookieName)) return noop

  const handleClick = (event) => {
    const { target } = event
    if (!(target instanceof Element)) return

    const source = resolveConsentSource(target)
    if (!source) return

    recordFirstInteraction(source, { analytics, cookieName })
    document.removeEventListener('click', handleClick, true)
  }

  document.addEventListener('click', handleClick, true)
  return () => document.removeEventListener('click', handleClick, true)
}
